import { computeEffectivePercent } from './dip-pipeline/dip-pipeline'
import type { FactorInputs } from './dip-pipeline/dip-pipeline.types'
import type { FactorWeights } from './dip-pipeline/dip-factors.schema'

// ===== min-spread ladder constants (single source of truth; the backend engine imports these) =====
// first-dip drop as a fraction of the daily range R (α)
export const MIN_SPREAD_FIRST_DIP_FRACTION = 0.5
// base averaging-down step as a fraction of R (β)
export const MIN_SPREAD_STEP_FRACTION = 0.5
// ladder steepness: the deepest rung's step is G× the base step (G)
export const MIN_SPREAD_LADDER_STEEPNESS = 4
// sell-chain step as a fraction of R (γ)
export const MIN_SPREAD_SELL_STEP_FRACTION = 0.5
// reachability threshold: warn when the rise needed to clear the floor exceeds κ×R (κ)
export const MIN_SPREAD_REACHABILITY_K = 1.0

// step (percent) to the NEXT rung given how many lots are already held: β·R·G^(lotsHeld/M)
export function minSpreadStepPct(R: number, M: number, lotsHeld: number): number {
  if (R <= 0 || M <= 0) return 0
  return MIN_SPREAD_STEP_FRACTION * R * Math.pow(MIN_SPREAD_LADDER_STEEPNESS, lotsHeld / M)
}

// the peak-pegged ladder of M arm prices: rung 1 sits α·R below the peak; each further rung an
// exponentially widening step below the previous one. mirrors the engine's minSpreadLadder().
export function buildMinSpreadLadder(peak: number, R: number, M: number): number[] {
  if (R <= 0 || M <= 0 || peak <= 0) return []
  const arms: number[] = []
  let arm = peak * (1 - (MIN_SPREAD_FIRST_DIP_FRACTION * R) / 100)
  arms.push(arm)
  for (let k = 1; k < M; k++) {
    arm = arm * (1 - minSpreadStepPct(R, M, k) / 100)
    arms.push(arm)
  }
  return arms
}

// smallest sell/avg price ratio whose net (after both commissions and tax) clears minSpreadPct.
// r_min = ((1 + c) + s/(1 − t)) / (1 − c). mirrors the engine's rMin().
export function rMin(minSpreadPct: number, commissionRate: number, taxRate: number): number {
  const s = minSpreadPct / 100
  const afterTax = taxRate < 1 ? 1 - taxRate : 1
  return (1 + commissionRate + s / afterTax) / (1 - commissionRate)
}

// whether the rise needed to clear the min-spread floor stays within a typical day's move (κ·R).
// mirrors the engine's minSpreadReachable(); returns null when R is unknown.
export function minSpreadReachable(
  minSpreadPct: number,
  commissionRate: number,
  taxRate: number,
  R: number
): boolean | null {
  if (R <= 0) return null
  return (rMin(minSpreadPct, commissionRate, taxRate) - 1) * 100 <= MIN_SPREAD_REACHABILITY_K * R
}

// ===== ladder plan (the full remaining buy + sell rungs the bar renders) =====

// one rung on the ladder: a planned arm price, the step % that produced it, its 1-based position in
// the leg (nearest = 1), and how many lots fill there (1, except a sell-from-average leg that exits
// the whole position at one price).
export interface LadderRung {
  price: number
  stepPct: number
  lotIndex: number
  lots: number
}

export interface LadderPlan {
  buys: LadderRung[]
  sells: LadderRung[]
}

// everything the projection needs, drawn from the live session-state DTO + the instrument's effective
// settings on the frontend, or the engine's own state on the backend.
export interface LadderPlanInput {
  mode: 'manual' | 'minSpread'
  currentPrice: number
  peak: number
  lotsHeld: number
  maxHeldLots: number
  avgEntry: number | null
  lastBuyPrice: number | null
  lastSellPrice: number | null
  // 30-day mean daily range % (R); null/0 leaves the min-spread buy ladder inert
  volatilityRangePct: number | null
  // gates: derive from buyArmPrice != null / sellArmPrice != null (read-only bar) or recompute (sim)
  buyActive: boolean
  sellActive: boolean
  // min-spread inputs
  minSpreadPct: number | null
  commissionRate: number
  taxRate: number
  // manual inputs
  dropPct: number
  dropPctMax: number
  shiftPct: number
  shiftPctMax: number
  risePct: number
  sellShiftPct: number
  sellFromAverage: boolean
  // factor pipeline (manual effective drop/shift)
  factorWeights: FactorWeights
  moodRate: number
  desireRate: number
  lotSize: number
}

// factor inputs at a hypothetical price — mirrors DayTradeSession.factorInputs (single-instrument
// context: disproportion / freeCash / accountCash are inert).
function factorInputsAt(input: LadderPlanInput, price: number): FactorInputs {
  const avg = input.avgEntry
  return {
    disproportion: null,
    avgCostDistance: avg !== null && avg > 0 ? ((price - avg) / avg) * 100 : null,
    freeCash: null,
    accountCash: null,
    lotPrice: input.lotSize * price,
    mood: input.moodRate,
    desire: input.desireRate,
  }
}

function bandOf(min: number, max: number): { min: number; max: number } {
  return { min, max: max > min ? max : min }
}

function effectiveDropPct(input: LadderPlanInput, price: number): number {
  return computeEffectivePercent(
    factorInputsAt(input, price),
    input.factorWeights,
    bandOf(input.dropPct, input.dropPctMax),
    'drop'
  ).value
}

function effectiveShiftPct(input: LadderPlanInput, price: number): number {
  return computeEffectivePercent(
    factorInputsAt(input, price),
    input.factorWeights,
    bandOf(input.shiftPct, input.shiftPctMax),
    'shift'
  ).value
}

function buildBuyLadder(input: LadderPlanInput): LadderRung[] {
  if (!input.buyActive) return []
  const remaining = input.maxHeldLots - input.lotsHeld
  if (remaining <= 0) return []

  // min-spread: exact peak-pegged ladder, sliced to the unfilled rungs
  if (input.mode === 'minSpread') {
    const R = input.volatilityRangePct ?? 0
    const arms = buildMinSpreadLadder(input.peak, R, input.maxHeldLots)
    if (arms.length === 0) return []
    const rungs: LadderRung[] = []
    for (let k = input.lotsHeld; k < arms.length; k++) {
      rungs.push({
        price: arms[k],
        stepPct: minSpreadStepPct(R, input.maxHeldLots, k),
        lotIndex: k + 1,
        lots: 1,
      })
    }
    return rungs
  }

  // manual: rung 0 matches the engine's buyArmThreshold; deeper rungs project the shift chain,
  // holding the effective shift at its rung-0 value (factor-pipeline drift on deep rungs is ignored).
  const rungs: LadderRung[] = []
  let firstPrice: number
  let firstStep: number
  if (input.lotsHeld === 0 || input.lastBuyPrice === null) {
    firstStep = effectiveDropPct(input, input.currentPrice)
    firstPrice = input.peak * (1 - firstStep / 100)
  } else {
    firstStep = effectiveShiftPct(input, input.lastBuyPrice)
    firstPrice = input.lastBuyPrice * (1 - firstStep / 100)
  }
  rungs.push({ price: firstPrice, stepPct: firstStep, lotIndex: input.lotsHeld + 1, lots: 1 })
  const shiftHeld = effectiveShiftPct(input, firstPrice)
  let prev = firstPrice
  for (let i = 1; i < remaining; i++) {
    prev = prev * (1 - shiftHeld / 100)
    rungs.push({ price: prev, stepPct: shiftHeld, lotIndex: input.lotsHeld + i + 1, lots: 1 })
  }
  return rungs
}

function buildSellLadder(input: LadderPlanInput): LadderRung[] {
  if (!input.sellActive || input.lotsHeld <= 0 || input.avgEntry === null) return []

  if (input.mode === 'minSpread') {
    const R = input.volatilityRangePct ?? 0
    const floor =
      input.avgEntry * rMin(input.minSpreadPct ?? 0, input.commissionRate, input.taxRate)
    const stepPct = MIN_SPREAD_SELL_STEP_FRACTION * R
    const first =
      input.lastSellPrice !== null
        ? Math.max(floor, input.lastSellPrice * (1 + stepPct / 100))
        : floor
    return chainUp(first, stepPct, input.lotsHeld)
  }

  // manual, sell-from-average: the whole position exits at one price
  if (input.sellFromAverage) {
    const price = input.avgEntry * (1 + input.risePct / 100)
    return [{ price, stepPct: input.risePct, lotIndex: 1, lots: input.lotsHeld }]
  }

  const first =
    input.lastSellPrice !== null
      ? input.lastSellPrice * (1 + input.sellShiftPct / 100)
      : input.avgEntry * (1 + input.risePct / 100)
  return chainUp(first, input.sellShiftPct, input.lotsHeld)
}

// a geometric chain upward from `first` by `stepPct` for `count` rungs (one lot each)
function chainUp(first: number, stepPct: number, count: number): LadderRung[] {
  const rungs: LadderRung[] = []
  let price = first
  for (let j = 0; j < count; j++) {
    if (j > 0) price = price * (1 + stepPct / 100)
    rungs.push({ price, stepPct, lotIndex: j + 1, lots: 1 })
  }
  return rungs
}

// the full plan the bar renders: every remaining buy rung (down to saturation) and every held-lot
// sell rung (stepping up). rung 0 of each leg equals the engine's next arm by construction.
export function buildLadderPlan(input: LadderPlanInput): LadderPlan {
  return { buys: buildBuyLadder(input), sells: buildSellLadder(input) }
}
